import { useCallback, useEffect, useRef } from 'preact/hooks';

import { type Game } from '@/shared/api/game/types';
import { type UseWsReturnType } from '@/shared/hooks/use-ws';
import { MessageAction, MessageType } from '@/shared/hooks/use-ws/types';
import {
  useAppActions,
  useAppState,
  useAppStateRaw,
} from '@/shared/stores/app-store/app-store';

const FIGHT_SCORE_TIMER = 15000;

export function useFightScore(ws: UseWsReturnType, game: Game | null) {
  const { hydrate } = useAppActions();
  const { wsSentInitialMsg } = useAppState();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const startedRef = useRef(false); // tracks if we've already started
  const { isLiteMode, isExclusiveMode } = useAppStateRaw.getState();

  const fightScoreWSHandler = useCallback(
    ({ payload }: { payload: { myScore: number } }) => {
      hydrate((state) => {
        if (!state.game?.fight) return {};
        return {
          game: {
            ...state.game,
            fight: {
              ...state.game.fight,
              me: { ...state.game.fight.me, score: payload.myScore },
            },
          },
        };
      });
    },
    [hydrate]
  );

  // Subscribe to WebSocket FIGHT_SCORE messages
  useEffect(() => {
    if (!wsSentInitialMsg || isLiteMode || isExclusiveMode) return;

    const subscriptionId = ws.subscribe(MessageType.FIGHT_SCORE, fightScoreWSHandler);
    return () => {
      ws.unsubscribe(MessageType.FIGHT_SCORE, subscriptionId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsSentInitialMsg, fightScoreWSHandler]);

  // Reset the startedRef and clean up interval on ws disconnect
  useEffect(() => {
    if (!wsSentInitialMsg) {
      startedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [wsSentInitialMsg]);

  // Send EXECUTE messages on interval
  useEffect(() => {
    if (!wsSentInitialMsg || !game || startedRef.current || isExclusiveMode || isLiteMode)
      return;

    startedRef.current = true;

    const sendFightScoreMessage = (gameKey: string) =>
      ws.sendMessage(MessageType.FIGHT_SCORE, MessageAction.EXECUTE, {
        gameKey,
      });

    // Send immediately
    sendFightScoreMessage(game.key);

    // Setup interval
    intervalRef.current = setInterval(() => {
      sendFightScoreMessage(game.key);
    }, FIGHT_SCORE_TIMER);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      startedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsSentInitialMsg, game?.key]);
}
